import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { LayoutGrid, ShoppingCart, Package, ArrowRight, Upload, CheckCircle, Clock, Truck } from 'lucide-react';
import { motion } from 'framer-motion';
interface DeliverPageProps {
  onOpenSampleModal?: () => void;
}
const standardSteps = [{
  icon: LayoutGrid,
  title: 'Browse and select',
  description: 'Search our full range by category, application, or keyword',
  primaryCta: {
    label: 'View products',
    href: '/products'
  },
  secondaryCta: {
    label: 'Learn more',
    href: '#'
  }
}, {
  icon: ShoppingCart,
  title: 'Add to basket and checkout securely',
  description: 'Complete payment in minutes with instant order confirmation',
  primaryCta: null,
  secondaryCta: {
    label: 'Learn more',
    href: '#'
  }
}, {
  icon: Package,
  title: 'Receive your order within 5–7 working days',
  description: 'We handle production and dispatch, you get tracking updates',
  primaryCta: null,
  secondaryCta: {
    label: 'Learn more',
    href: '#'
  }
}];
const bespokeSteps = [{
  number: '01',
  label: 'Bespoke',
  title: 'Custom enquiry and approval',
  description: "Tell us what you need. We'll guide you through specifications, materials, and finishes to match your requirements exactly.",
  primaryCta: {
    label: 'Enquire',
    href: '/contact'
  },
  secondaryCta: {
    label: 'Learn more',
    href: '#'
  }
}, {
  number: '02',
  label: 'Artwork',
  title: 'Upload and review your design',
  description: "Send us your artwork or let us create it for you. We'll prepare a digital proof for your approval before we begin production.",
  primaryCta: {
    label: 'Upload',
    href: '/resources'
  },
  secondaryCta: {
    label: 'Learn more',
    href: '#'
  }
}, {
  number: '03',
  label: 'Quotation',
  title: 'Receive your pricing and timeline',
  description: "Once we've reviewed your requirements, we'll send a detailed quote with production lead time and delivery schedule.",
  primaryCta: {
    label: 'Request quote',
    href: '/contact'
  },
  secondaryCta: {
    label: 'Learn more',
    href: '#'
  }
}, {
  number: '04',
  label: 'Production',
  title: 'We manufacture to your exact specifications',
  description: 'Your order goes into production using our state-of-the-art equipment. Quality checks are performed at every stage before dispatch.',
  primaryCta: null,
  secondaryCta: {
    label: 'Learn more',
    href: '#'
  }
}];
const trustBadges = [{
  icon: Truck,
  label: 'Free nationwide delivery'
}, {
  icon: Clock,
  label: '5–7 day standard lead time'
}, {
  icon: CheckCircle,
  label: 'Right first time guarantee'
}];
export function DeliverPage({
  onOpenSampleModal
}: DeliverPageProps) {
  return <div className="min-h-screen bg-white font-sans text-[#0A1628] selection:bg-[#0052CC] selection:text-white overflow-x-hidden" data-id="element-970">
      <Header onOpenSampleModal={onOpenSampleModal} data-id="element-971" />

      <main className="pt-[135px] md:pt-[145px]" data-id="element-972">
        {/* Hero with background */}
        <section className="relative py-20 md:py-28 bg-[#0A1628] overflow-hidden" data-id="element-973">
          {/* Background image overlay */}
          <div className="absolute inset-0" data-id="element-974">
            <img src="https://cdn.magicpatterns.com/uploads/qszDzSmxSCirrNe3REUbpj/nameplates-industial.webp" alt="" className="w-full h-full object-cover opacity-10" data-id="element-975" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628] via-[#0A1628]/90 to-[#0A1628]/70" data-id="element-976" />
          </div>

          <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative" data-id="element-977">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="max-w-2xl" data-id="element-978">
              <span className="text-xs font-bold text-[#0052CC] uppercase tracking-widest mb-4 block" data-id="element-979">
                Delivery & Process
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.08] mb-6" data-id="element-980">
                How we deliver
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed mb-10" data-id="element-981">
                Understand our ordering process, timelines, and what to expect
                from start to finish
              </p>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-4" data-id="element-982">
                {trustBadges.map(badge => {
                const Icon = badge.icon;
                return <div key={badge.label} className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2" data-id="element-983">
                      <Icon className="h-4 w-4 text-[#0052CC]" data-id="element-984" />
                      <span className="text-sm font-medium text-white" data-id="element-985">
                        {badge.label}
                      </span>
                    </div>;
              })}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Standard Steps - 3 cards FIRST and prominent */}
        <section className="py-16 md:py-20 bg-white" data-id="element-986">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-987">
            <div className="text-center mb-12" data-id="element-988">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 block" data-id="element-989">
                Standard
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight mb-3" data-id="element-990">
                Off-the-shelf ordering
              </h2>
              <p className="text-lg text-slate-500" data-id="element-991">
                Fast, straightforward purchasing for ready-made products
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-992">
              {standardSteps.map((step, index) => {
              const Icon = step.icon;
              return <motion.div key={step.title} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="bg-white border-2 border-slate-100 rounded-2xl p-8 flex flex-col hover:border-[#0052CC]/20 hover:shadow-lg transition-all duration-300" data-id="element-993">
                    <div className="flex items-center justify-between mb-6" data-id="element-994">
                      <div className="h-12 w-12 bg-[#0052CC]/10 rounded-xl flex items-center justify-center" data-id="element-995">
                        <Icon className="h-6 w-6 text-[#0052CC]" data-id="element-996" />
                      </div>
                      <span className="text-3xl font-extrabold text-slate-100" data-id="element-997">
                        0{index + 1}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3" data-id="element-998">
                      {step.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-6 flex-1" data-id="element-999">
                      {step.description}
                    </p>
                    <div className="flex items-center gap-3" data-id="element-1000">
                      {step.primaryCta && <a href={step.primaryCta.href} className="px-4 py-2 bg-[#0A1628] text-white text-sm font-semibold rounded-full hover:bg-[#0052CC] transition-colors" data-id="element-1001">
                          {step.primaryCta.label}
                        </a>}
                      <a href={step.secondaryCta.href} className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-[#0052CC] transition-colors" data-id="element-1002">
                        {step.secondaryCta.label}{' '}
                        <ArrowRight className="ml-1 h-3.5 w-3.5" data-id="element-1003" />
                      </a>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* Visual divider with image */}
        <section className="relative h-64 md:h-80 overflow-hidden" data-id="element-1004">
          <img src="https://cdn.magicpatterns.com/uploads/4GroWq4twSqQJRdxL7S1mT/sample_3.webp" alt="Industrial labels production" className="w-full h-full object-cover" data-id="element-1005" />
          <div className="absolute inset-0 bg-[#0A1628]/60 flex items-center justify-center" data-id="element-1006">
            <div className="text-center" data-id="element-1007">
              <p className="text-white/70 text-sm font-semibold uppercase tracking-widest mb-2" data-id="element-1008">
                Need something bespoke?
              </p>
              <h3 className="text-2xl md:text-3xl font-extrabold text-white" data-id="element-1009">
                Custom orders, handled end to end
              </h3>
            </div>
          </div>
        </section>

        {/* Bespoke Process - Numbered Steps */}
        <section className="py-16 md:py-24 bg-slate-50" data-id="element-1010">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1011">
            <div className="text-center mb-16" data-id="element-1012">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 block" data-id="element-1013">
                Bespoke
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight mb-3" data-id="element-1014">
                Custom enquiry and approval
              </h2>
              <p className="text-lg text-slate-500 max-w-xl mx-auto" data-id="element-1015">
                For custom requirements, we guide you through every step from
                enquiry to delivery
              </p>
            </div>

            <div className="max-w-3xl mx-auto" data-id="element-1016">
              {bespokeSteps.map((step, index) => <motion.div key={step.number} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="flex gap-8 md:gap-12 mb-16 last:mb-0" data-id="element-1017">
                  <div className="flex-shrink-0" data-id="element-1018">
                    <span className="text-[80px] md:text-[100px] font-extrabold text-slate-100 leading-none select-none" data-id="element-1019">
                      {step.number}
                    </span>
                  </div>
                  <div className="flex-1 pt-4" data-id="element-1020">
                    <div className="w-8 h-0.5 bg-[#0052CC] mb-3" data-id="element-1021"></div>
                    <span className="text-xs font-bold text-[#0052CC] uppercase tracking-widest mb-3 block" data-id="element-1022">
                      {step.label}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-4 leading-tight" data-id="element-1023">
                      {step.title}
                    </h3>
                    <p className="text-slate-500 leading-relaxed mb-6" data-id="element-1024">
                      {step.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-3" data-id="element-1025">
                      {step.primaryCta && <a href={step.primaryCta.href} className="px-5 py-2.5 bg-[#0A1628] text-white text-sm font-semibold rounded-full hover:bg-[#0052CC] transition-colors" data-id="element-1026">
                          {step.primaryCta.label}
                        </a>}
                      <a href={step.secondaryCta.href} className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-[#0052CC] transition-colors" data-id="element-1027">
                        {step.secondaryCta.label}{' '}
                        <ArrowRight className="ml-1 h-3.5 w-3.5" data-id="element-1028" />
                      </a>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-16 bg-[#0052CC]" data-id="element-1029">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center" data-id="element-1030">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-1031">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1032">
                Ready to place your order?
              </h2>
              <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto" data-id="element-1033">
                Browse our full range of standard products or get in touch to
                discuss a bespoke requirement.
              </p>
              <div className="flex flex-wrap justify-center gap-3" data-id="element-1034">
                <a href="/products" className="px-7 py-3.5 bg-white text-[#0052CC] font-bold rounded-full hover:bg-slate-100 transition-colors" data-id="element-1035">
                  Browse products
                </a>
                <a href="/contact" className="px-7 py-3.5 bg-white/10 border border-white/30 text-white font-bold rounded-full hover:bg-white hover:text-[#0052CC] transition-all" data-id="element-1036">
                  Contact us
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1037" />
    </div>;
}